import React, { useState, useEffect } from 'react';
import { User } from 'lucide-react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import { useUpdateProfile } from '../../hooks/useProfile';
import { useLanguage } from '../../lang/LangContext';
import { KEYS } from '../../lang/keys';
import { normalizeFieldErrors } from '../../services/api';

const EditProfileModal = ({ isOpen, onClose, user, onProfileUpdated }) => {
    const [formData, setFormData] = useState({
        name: '',
    });
    const [errors, setErrors] = useState({});
    const [apiError, setApiError] = useState('');
    const [loading, setLoading] = useState(false);
    const updateProfile = useUpdateProfile();
    const { t } = useLanguage();

    useEffect(() => {
        if (user && isOpen) {
            setFormData({
                name: user.name || '',
            });
            setErrors({});
            setApiError('');
        }
    }, [user, isOpen]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = t(KEYS.EDIT_PROFILE_NAME_REQUIRED);
        } else if (formData.name.length > 255) {
            newErrors.name = t(KEYS.COMMON_ERROR_MAX_CHARS);
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setApiError('');

        if (!validateForm()) {
            return;
        }

        setLoading(true);

        try {
            await updateProfile.mutateAsync({
                name: formData.name.trim(),
            });
            if (onProfileUpdated) onProfileUpdated();
            onClose();
        } catch (err) {
            if (err.code === 'VALIDATION_ERROR' && err.errors) {
                setErrors(normalizeFieldErrors(err.errors));
            } else {
                setApiError(err.message || t(KEYS.EDIT_PROFILE_ERROR));
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={t(KEYS.EDIT_PROFILE_TITLE)}
            subtitle="Update your profile information"
            size="medium"
            footer={
                <>
                    <Button variant="outline" onClick={onClose} disabled={loading}>
                        {t(KEYS.COMMON_CANCEL)}
                    </Button>
                    <Button variant="primary" onClick={handleSubmit} disabled={loading}>
                        {loading ? t(KEYS.EDIT_PROFILE_SUBMITTING) : t(KEYS.EDIT_PROFILE_SUBMIT)}
                    </Button>
                </>
            }
        >
            <form onSubmit={handleSubmit} className="space-y-5">
                {apiError && (
                    <div className="p-3 bg-status-overdue-bg text-status-overdue rounded text-sm animate-appear">
                        {apiError}
                    </div>
                )}

                <div className="space-y-1">
                    <label htmlFor="name" className="label-base">
                        Name <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                            type="text"
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            className={`input-base pl-10 ${errors.name ? 'border-red-500 focus:ring-red-500' : ''}`}
                            disabled={loading}
                            maxLength={255}
                            placeholder="Your name"
                        />
                    </div>
                    {errors.name && (
                        <p className="text-xs text-red-500 mt-1">{errors.name}</p>
                    )}
                </div>
            </form>
        </Modal>
    );
};

export default EditProfileModal;
